import { Injectable, NgZone } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ColumnFilter, Table } from 'primeng/table';
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { TableExtended } from '../../shared/model/primeng-extensions.model';
import { Utils } from '../utils';
import { TieredMenu } from "primeng/tieredmenu";
import { AppStateService } from './app-state.service';
import { TreeTable } from "primeng/treetable";
import { Dialog } from "primeng/dialog";
import { AnimationEvent } from "@angular/animations";

@Injectable({
	providedIn: 'root'
})
export class PrimeNGHelpersService {
	constructor(
		private ngZone: NgZone,
		private messageService: MessageService
	) { }

	static filterTable(table: Table, eventOrText: Event | string, matchMode = 'contains') {
		const value = typeof eventOrText == 'string' ?
			eventOrText :
			(eventOrText.target as HTMLInputElement).value;

		table.filterGlobal(value, matchMode);
	}

	/**
	 * Modifies original source code to prevent loss of scroll position when a scroll reset isn't needed.
	 * See: https://github.com/primefaces/primeng/blob/c3ae667/src/app/components/table/table.ts#L1543
	 */
	enableTableSuppressScrollTop(table: TableExtended) {
		const orig = table.resetScrollTop.bind(table);

		table.resetScrollTop = () => {
			if (table.suppressResetScrollTop)
				table.suppressResetScrollTop = false;
			else
				orig();
		}
	}

	// TODO - Redesign when AJS is gone
	ajsSuccessfulSaveToast(typeOfData: string, routeTitle: string) {
		if (typeOfData)
			typeOfData = Utils.capitalizeFirstLetter(typeOfData.replace(/s$/, ''));
		else {
			typeOfData =
				routeTitle.includes('Note') ? 'Note' :
				routeTitle.includes('Report') ? 'Report' : 
				routeTitle;
		}
		
		this.ajsSuccessToast(`${typeOfData} data saved successfully.`)
	}
	
	ajsSuccessToast(message: string) {
		this.messageService.add({
			severity: 'success',
			summary: 'Success',
			detail: message,
			life: 3000
		});
	}

	showErrorToast(error: string, title = 'Error', sticky = false) {
		error = AppStateService.isProdEnvironment && !AppStateService.isAdmin && !AppStateService.isSwitchedUser ?
			`Unfortunately, an error has occurred with something you were just doing! We've recorded the issue, and we're 
			sorry for the inconvenience.\n\nPlease contact LogBook support if this error continues.` :
			`Non-prod environment or admin/switched user - showing real error:\n\n${error}\n\nSee console or logged errors for more details.`;

		this.ngZone.run(() => {
			this.messageService.add({
				key: 'error-toast',
				severity: 'error',
				summary: title,
				detail: error,
				life: 5000,
				sticky: sticky
			});
		})
	}

	/**
	 * When doing server-side filtering only by using a custom ng-template inside a p-columnFilter, this method can be used to
	 * overwrite the filter's default "Clear" and "Apply" button handlers. An optional onFilterHidden callback can be supplied 
	 * to run any logic needed after the filter overlay is hidden, such as restoring the filter editor to the last valid input 
	 * when invalid characters were entered. Just pass in handlers with what needs to happen in your component.
	 */
	registerServerSideColumnFilter(
		columnFilter: ColumnFilter,
		onApplyClicked: (matchMode: string) => boolean,
		onClearClicked: () => void,
		onFilterHidden?: (clearClicked: boolean) => void
	) {
		let clearFilterClicked = false;
		
		columnFilter.applyFilter = () => {
			const hasFilter = onApplyClicked(columnFilter.fieldConstraints[0].matchMode!);
			
			columnFilter.hasFilter = () => hasFilter;
			columnFilter.hide();
		}

		columnFilter.clearFilter = () => {
			clearFilterClicked = true;
			onClearClicked();
			
			columnFilter.hasFilter = () => false;
			columnFilter.hide();
		}

		if (onFilterHidden) {
			const primeNgOnOverlayHide = columnFilter.onOverlayHide.bind(columnFilter);

			columnFilter.onOverlayHide = () => {
				primeNgOnOverlayHide();
				onFilterHidden(clearFilterClicked);

				clearFilterClicked = false;
			}
		}
	}
	
	/**
	 * Creates an observable for whether a tiered menu is opened due to no
	 * native observable or event being offered via the component.
	 */
	static tieredMenuVisible(menu: TieredMenu): Observable<boolean> {
		const origShow = menu.show.bind(menu);
		const origHide = menu.hide.bind(menu);
		const ob$ = new Subject<boolean>()
		
		menu.show = (event: any) => {
			origShow(event)
			ob$.next(menu.visible)
		}
		
		menu.hide = () => {
			origHide();
			ob$.next(menu.visible)
		}
		
		return ob$
	}
	
	static disableDeselectionOfRow(table: Table) {
		const aTable = table as any;
		if (aTable._hadTableDeselectionUpdate) return;
		aTable._hadTableDeselectionUpdate = true;

		const original = table.handleRowClick.bind(table);
		table.handleRowClick = function(this: Table, event: any) {
			let data = event.rowData;
			if (this.selectionMode === 'single' && this.isSelected(data)) return;
			original(event);
		}.bind(table);
	}

	static disableTreeTableDeselection(table: TreeTable) {
		const aTable = table as any;
		if (aTable._hadTableDeselectionUpdate) return;
		aTable._hadTableDeselectionUpdate = true;

		const original = table.handleRowClick.bind(table);
		table.handleRowClick = function(this: TreeTable, event: any) {
			if (this.selectionMode === 'single' && this.isSelected(event.rowNode.node)) return;
			original(event);
		}.bind(table);
	}
	
	static onDialogContainerReady(dialog: Dialog, func: (element: HTMLDivElement) => void) {
		const rDialog = dialog as (Dialog & { _onDialogReady?: ((element: HTMLDivElement) => void)[] });
		
		if (rDialog.container) {
			func(dialog.container);
		}
		
		if (Array.isArray(rDialog._onDialogReady)) {
			if (!!dialog.container)
				func(dialog.container);
			else
				rDialog._onDialogReady.push(func);
			
			return;
		}
		
		rDialog._onDialogReady = [func];
		
		// Overrides the onAnimationStart method as this is when pDialog sets its container.
		const originalOnAnimation = dialog.onAnimationStart.bind(dialog);
		dialog.onAnimationStart = (event: AnimationEvent) => {
			originalOnAnimation(event);
			rDialog._onDialogReady?.forEach((fn) => fn(rDialog.container));
			delete rDialog._onDialogReady;
		};
	}
}